import { useCallback, useEffect, useState } from "react";

// Helper: Fetch wrapper with CSRF token
const fetchBackend = (url, options = {}) => {
  const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
  return fetch(url, {
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    ...options,
  });
};

// Hook: Manages CRUD operations for Marketing list entries
function useMarketingList(indexUrl, entityUrl, entityKey) {
  const [entries, setEntries] = useState([]); // List of entries
  const [loading, setLoading] = useState(false); // Loading state
  const [error, setError] = useState(null); // Error state

  // Fetch all entries from the backend
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchBackend(indexUrl);
      const data = await response.json();
      setEntries(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [indexUrl]);

  // Create a new entry
  const createEntry = useCallback(
    async (entry) => {
      try {
        const response = await fetchBackend(indexUrl, {
          method: "POST",
          body: JSON.stringify({ [entityKey]: entry }),
        });
        if (response.ok) {
          fetchAll();
        } else if (response.status === 422) {
          const body = await response.json();
          throw new Error(body.errors.join(", "));
        } else {
          throw new Error("Failed to create an entry");
        }
      } catch (err) {
        setError(err.message);
      }
    },
    [indexUrl, fetchAll, entityKey],
  );

  // Update an existing entry
  const updateEntry = useCallback(
    async (id, data) => {
      try {
        const response = await fetchBackend(entityUrl.replace(":id", id), {
          method: "PATCH",
          body: JSON.stringify(data),
        });
        if (response.ok) {
          fetchAll();
        } else if (response.status === 422) {
          const body = await response.json();
          throw new Error(body.errors.join(", "));
        } else {
          throw new Error("Failed to update the entry");
        }
      } catch (err) {
        setError(err.message);
      }
    },
    [entityUrl, fetchAll],
  );

  // Delete an entry
  const destroyEntry = useCallback(
    async (id) => {
      try {
        const response = await fetchBackend(entityUrl.replace(":id", id), {
          method: "DELETE",
        });
        if (response.ok) fetchAll();
        else throw new Error("Failed to delete entry");
      } catch (err) {
        setError(err.message);
      }
    },
    [entityUrl, fetchAll],
  );

  useEffect(() => {
    fetchAll(); // Load all entries on mount
  }, [fetchAll]);

  return {
    entries,
    loading,
    error,
    createEntry,
    updateEntry,
    destroyEntry,
  };
}

export default useMarketingList;
